import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/resources";
import { getResourceById } from "@/utils/content";

const slides = heroSlides
  .map((id) => getResourceById(id))
  .filter((item): item is NonNullable<typeof item> => Boolean(item));

export default function HeroSlider() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  const touchStartX = useRef(0);

  const goPrev = () => setActive((prev) => (prev - 1 + slides.length) % slides.length);
  const goNext = () => setActive((prev) => (prev + 1) % slides.length);

  return (
    <section aria-label="焦点内容推荐" className="relative">
      <div
        className="relative h-[480px] md:h-[400px] rounded-lg overflow-hidden bg-primary-950"
        onTouchStart={(event) => {
          touchStartX.current = event.touches[0].clientX;
        }}
        onTouchEnd={(event) => {
          const diff = event.changedTouches[0].clientX - touchStartX.current;
          if (Math.abs(diff) > 40) {
            if (diff > 0) goPrev();
            else goNext();
          }
        }}
      >
        {slides.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
            }`}
          >
            <div className="absolute inset-0 md:left-auto md:right-0 md:w-[44%]">
              <img
                src={item.cover}
                alt={`${item.title} 焦点推荐封面`}
                title={`${item.title} 高清资源`}
                className="w-full h-full object-cover object-top opacity-30 md:opacity-100"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/92 to-primary-950/45 md:to-transparent"></div>

            <div className="relative z-10 h-full flex flex-col justify-center px-5 md:px-10 lg:px-12 max-w-[600px]">
              <p className="flex items-center gap-2 text-xs text-accent-300 mb-3">
                <span className="chip bg-accent-500 text-background-50">焦点推荐</span>
                <span className="text-background-300">本周精选 · {item.year}</span>
              </p>
              <h2 className="font-heading font-extrabold text-2xl md:text-4xl text-background-50 leading-tight">
                {item.title}
              </h2>
              <p className="mt-3 text-sm md:text-base text-background-200 leading-7 line-clamp-2 md:line-clamp-3">
                {item.summary}
              </p>
              <div className="mt-4 flex items-center gap-3 text-xs text-background-300 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-400" aria-hidden="true"></i>
                  {item.rating.toFixed(1)} 分
                </span>
                <span className="text-primary-700">|</span>
                <span>{item.region}</span>
                <span className="text-primary-700">|</span>
                <span>{item.type}</span>
                <span className="text-primary-700">|</span>
                <span>{item.quality}</span>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <Link
                  to={`/detail/${item.id}`}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
                >
                  查看详情
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
                <Link
                  to={`/category/${item.category}`}
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-primary-700 hover:border-accent-400 text-background-100 hover:text-accent-300 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
                >
                  同类资源
                </Link>
              </div>
            </div>
          </div>
        ))}

        <div className="absolute z-20 bottom-4 right-4 flex items-center gap-2">
          <button
            type="button"
            onClick={goPrev}
            aria-label="上一个焦点内容"
            className="w-8 h-8 md:w-9 md:h-9 rounded-md bg-background-50/15 border border-background-50/25 text-background-50 flex items-center justify-center cursor-pointer hover:bg-background-50/25 transition-colors"
          >
            <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            onClick={goNext}
            aria-label="下一个焦点内容"
            className="w-8 h-8 md:w-9 md:h-9 rounded-md bg-background-50/15 border border-background-50/25 text-background-50 flex items-center justify-center cursor-pointer hover:bg-background-50/25 transition-colors"
          >
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          </button>
        </div>

        <div className="absolute z-20 bottom-4 left-5 md:left-10 lg:left-12 flex items-center gap-1.5">
          {slides.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActive(index)}
              aria-label={`切换到 ${item.title}`}
              aria-current={index === active ? "true" : undefined}
              className={`h-1.5 rounded-full cursor-pointer transition-all ${
                index === active ? "w-7 bg-accent-500" : "w-3 bg-background-50/40 hover:bg-background-50/70"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}